import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, Send } from 'lucide-react';
import { FirestoreService } from '../services/firestore';
import { MathUtils } from '../components/math/MathUtils';
import GeometryTool from '../components/tools/GeometryTool';
import { useAuthStore } from '../stores/authStore';
import logo from '../assets/images/logo.png';
import PdfDocumentPanel from '../components/document/PdfDocumentPanel';
import DocumentNarrationPlayer from '../components/document/DocumentNarrationPlayer';
import ResponseAreaRenderer from '../components/document/ResponseAreaRenderer';
import AuthModal from '../components/auth/AuthModal';
import useMetaTags from '../hooks/useMetaTags';
import { createPortal } from 'react-dom';

const V2_REGION_MIN_SPACE_PX = 20;

const getSharedPageLayout = (pageHtml, pageIndex, pageCount) => {
  const container = window.document.createElement('div');
  container.innerHTML = pageHtml || '';
  const header = container.querySelector('.doc-header');
  const footer = container.querySelector('.doc-footer');
  const savedTopSpace = Number(header?.getAttribute('data-v2-region-space-px'));
  const savedBottomSpace = Number(footer?.getAttribute('data-v2-region-space-px'));

  const pageNumber = footer?.querySelector('.page-number[data-page-number]');
  if (pageNumber) {
    const parsedStart = Number.parseInt(pageNumber.getAttribute('data-page-number-start') || '1', 10);
    const startNumber = Number.isFinite(parsedStart) ? Math.max(1, parsedStart) : 1;
    pageNumber.textContent = `Page ${startNumber + pageIndex} of ${startNumber + pageCount - 1}`;
  }

  const headerHtml = header?.outerHTML || '';
  const footerHtml = footer?.outerHTML || '';
  header?.remove();
  footer?.remove();

  return {
    headerHtml,
    footerHtml,
    bodyHtml: container.innerHTML,
    topSpacePx: Number.isFinite(savedTopSpace) && savedTopSpace > 0
      ? Math.max(V2_REGION_MIN_SPACE_PX, savedTopSpace)
      : V2_REGION_MIN_SPACE_PX,
    bottomSpacePx: Number.isFinite(savedBottomSpace) && savedBottomSpace > 0
      ? Math.max(V2_REGION_MIN_SPACE_PX, savedBottomSpace)
      : V2_REGION_MIN_SPACE_PX,
  };
};

const getAssessmentLabel = (assessment) => {
  if (!assessment?.enabled) return '';
  if (assessment.category === 'informal') {
    return assessment.informalType === 'self-practice'
      ? 'Informal • Self Practice'
      : 'Informal • Class Practice';
  }
  return 'Formal Assessment';
};

const SharedDocumentPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();
  const pageContainerRef = useRef(null);
  const handleLeaveSharedDocument = () => navigate(isAuthenticated ? '/' : '/login');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [document, setDocument] = useState(null);
  const [shareMeta, setShareMeta] = useState(null);
  const [responses, setResponses] = useState({});
  const [portalTargets, setPortalTargets] = useState([]);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  const [cloneError, setCloneError] = useState('');
  const [cloneRequested, setCloneRequested] = useState(false);
  const metaTitle = document?.title || document?.name || shareMeta?.documentName || 'Shared Document';
  const metaDescription =
    shareMeta?.shareDescription?.trim() ||
    `Open "${metaTitle}" in Sigma Classroom.`;
  const metaUrl = token && typeof window !== 'undefined'
    ? new URL(`/shared/${token}`, window.location.origin).toString()
    : '';

  useMetaTags(metaTitle, metaDescription, metaUrl);

  useEffect(() => {
    if (!token) {
      setError('No share token provided.');
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        const share = await FirestoreService.getShareByToken(token);
        if (!share) {
          setError('This shared link is invalid or has been removed.');
          setLoading(false);
          return;
        }
        if (share.shareType === 'user') {
          if (!isAuthenticated || !user?.uid) {
            setError('Sign in to access this shared item.');
            setLoading(false);
            return;
          }
          if (share.recipientId !== user.uid) {
            setError('You do not have access to this shared item.');
            setLoading(false);
            return;
          }
        }
        setShareMeta(share);
        const doc = await FirestoreService.getDocument(share.documentId);
        setDocument(doc);
      } catch (err) {
        console.error('Failed to load shared document:', err);
        setError('Failed to load the shared document.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isAuthenticated, token, user?.uid]);

  const assessment = document?.assessment || null;
  const isSharedSelfPracticeAssessment = assessment?.enabled
    && assessment?.category === 'informal'
    && assessment?.informalType === 'self-practice';
  const isSharedGradableAssessment = assessment?.enabled && !isSharedSelfPracticeAssessment;
  const assessmentLabel = getAssessmentLabel(assessment);
  const takePath = document?.id ? `/document/${document.id}/take` : '';
  const pages = useMemo(() => (
    Array.isArray(document?.pages) ? document.pages : []
  ), [document?.pages]);
  const sharedHeaderNarration = document?.narration?.url
    ? document.narration
    : document?.narration?.pages
      ? Object.values(document.narration.pages)[0] || null
      : null;
  const canRevealModelAnswers = Boolean(isAuthenticated);
  const modelAnswerHelperText = !isAuthenticated
    ? 'Sign in to reveal model answers after answering each response.'
    : 'Model answers can be revealed per response after you answer it.';
  const pageNodes = useMemo(
    () => pages.map((pageHtml, index) => {
      const {
        headerHtml,
        footerHtml,
        bodyHtml,
        topSpacePx,
        bottomSpacePx,
      } = getSharedPageLayout(pageHtml, index, pages.length);

      return (
        <div
          key={`shared-page-${index}`}
          className="shared-v2-document-page"
          style={{
            '--shared-v2-region-top-space': `${topSpacePx}px`,
            '--shared-v2-region-bottom-space': `${bottomSpacePx}px`,
          }}
          aria-label={`Page ${index + 1} of ${pages.length}`}
        >
          {headerHtml && (
            <div
              className="shared-v2-page-header"
              dangerouslySetInnerHTML={{ __html: headerHtml }}
            />
          )}
          <div
            className="shared-v2-page-body v2-model-editor-content"
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
          />
          {footerHtml && (
            <div
              className="shared-v2-page-footer"
              dangerouslySetInnerHTML={{ __html: footerHtml }}
            />
          )}
        </div>
      );
    }),
    [pages]
  );

  useEffect(() => {
    if (!pageContainerRef.current || !pages.length) {
      setPortalTargets([]);
      return;
    }

    const frameId = requestAnimationFrame(async () => {
      const container = pageContainerRef.current;
      if (!container) return;

      await MathUtils.renderMath(container);

      if (!isSharedSelfPracticeAssessment) {
        setPortalTargets([]);
        return;
      }

      const blocks = container.querySelectorAll('.response-area-block');
      const targets = [];
      blocks.forEach((el) => {
        const blockId = el.getAttribute('data-response-id');
        if (blockId) {
          el.removeAttribute('contenteditable');
          el.innerHTML = '';
          el.className = 'response-area-block response-area-active';
          targets.push({ blockId, element: el });
        }
      });
      setPortalTargets(targets);
    });

    if (!isSharedSelfPracticeAssessment) {
      setResponses({});
    }

    return () => cancelAnimationFrame(frameId);
  }, [document?.id, isSharedSelfPracticeAssessment, pages]);

  const handleResponseChange = useCallback((blockId, value) => {
    setResponses((prev) => ({ ...prev, [blockId]: { value } }));
  }, []);

  const handleOpenGradableAssessment = useCallback(() => {
    if (!takePath) return;
    if (isAuthenticated) {
      navigate(takePath);
      return;
    }
    navigate('/login', { state: { from: { pathname: takePath } } });
  }, [isAuthenticated, navigate, takePath]);

  const canCloneFromLink = shareMeta?.shareType === 'link'
    && shareMeta?.allowClone !== false
    && document?.isTemplate === true
    && document?.contentType !== 'geometry-diagram';

  const cloneSharedDocument = useCallback(async () => {
    if (!user?.uid || !document?.id) return;

    try {
      setIsCloning(true);
      setCloneError('');

      const sourceName = String(document?.name || document?.title || shareMeta?.documentName || 'Untitled Document').trim() || 'Untitled Document';

      const clonedId = await FirestoreService.createDocument(user.uid, {
        name: `Copy of ${sourceName}`,
        pages: Array.isArray(document?.pages) && document.pages.length > 0 ? document.pages : [''],
        folderPath: '',
        assessment: document?.assessment || null,
        contentType: document?.contentType === 'pdf' ? 'pdf' : 'editor',
        pdf: document?.pdf || null,
        narration: document?.narration || null,
        isTemplate: false,
        templateSourceId: document.id,
        templateSourceName: sourceName,
        clonedFromShareId: shareMeta?.id || null,
        clonedFromShareToken: token || null,
      });

      if (clonedId) {
        navigate(`/document/${clonedId}`);
      } else {
        setCloneError('Could not clone this document. Please try again.');
      }
    } catch (err) {
      console.error('Failed to clone shared document:', err);
      setCloneError('Could not clone this document. Please try again.');
    } finally {
      setIsCloning(false);
      setCloneRequested(false);
    }
  }, [document, navigate, shareMeta?.documentName, shareMeta?.id, token, user?.uid]);

  const handleCloneDocument = useCallback(() => {
    if (!canCloneFromLink || isCloning) return;
    setCloneError('');

    if (!isAuthenticated || !user?.uid) {
      setCloneRequested(true);
      setIsAuthModalOpen(true);
      return;
    }

    cloneSharedDocument();
  }, [canCloneFromLink, cloneSharedDocument, isAuthenticated, isCloning, user?.uid]);

  useEffect(() => {
    if (!cloneRequested || !isAuthenticated || !user?.uid || !document?.id || isCloning) return;
    cloneSharedDocument();
  }, [cloneRequested, cloneSharedDocument, document?.id, isAuthenticated, isCloning, user?.uid]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sigma-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 gap-4">
        <p className="text-gray-600 text-lg">{error}</p>
        <button
          onClick={handleLeaveSharedDocument}
          className="px-4 py-2 bg-sigma-600 text-white rounded-lg text-sm hover:bg-sigma-700"
        >
          Go to Home
        </button>
      </div>
    );
  }

  const isPdfDocument = document?.contentType === 'pdf' && !!document?.pdf?.url;
  const isGeometryDiagram = document?.contentType === 'geometry-diagram';

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <div className="flex shrink-0 flex-col gap-3 bg-gradient-to-r from-sigma-700 to-sigma-900 px-3 py-2 text-white sm:px-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          <button
            onClick={handleLeaveSharedDocument}
            className="p-2 hover:bg-white/10 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <img src={logo} alt="Sigma" className="h-8 w-auto" />
          <div className="w-px h-8 bg-white/20" />
          <div className="min-w-0">
            <h1 className="truncate text-sm font-semibold">
              {document?.title || document?.name || shareMeta?.documentName || 'Shared Document'}
            </h1>
            {shareMeta?.ownerName && (
              <p className="truncate text-xs text-white/70">Shared by {shareMeta.ownerName}</p>
            )}
            {assessmentLabel && (
              <p className="truncate text-xs text-white/70">{assessmentLabel}</p>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 lg:ml-3 lg:shrink-0">
          {sharedHeaderNarration?.url && (
            <DocumentNarrationPlayer
              narration={sharedHeaderNarration}
              variant="sharedHeader"
              title="Audio"
            />
          )}
          <span className="px-3 py-1 text-xs font-medium rounded-full bg-white/20">
            {isSharedSelfPracticeAssessment
              ? 'Assessment • Practice Mode'
              : isSharedGradableAssessment
                ? 'Assessment • Submit for Grading'
                : 'Read Only'}
          </span>
        </div>
      </div>

      {isSharedSelfPracticeAssessment && (
        <div className="mx-auto mt-4 w-full max-w-[210mm] rounded-xl border border-emerald-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-gray-900">Interactive self practice</div>
              <div className="text-xs text-gray-500">
                {modelAnswerHelperText}
              </div>
            </div>
          </div>
        </div>
      )}

      {canCloneFromLink && (
        <div className="mx-auto mt-4 w-full max-w-[210mm] rounded-xl border border-sigma-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-gray-900">Save your own copy</div>
              <div className="text-xs text-gray-500">
                Open the shared document first, then sign in only when you are ready to clone it.
              </div>
              {cloneError && (
                <div className="mt-1 text-xs text-red-600">{cloneError}</div>
              )}
            </div>
            <button
              type="button"
              onClick={handleCloneDocument}
              disabled={isCloning}
              className="inline-flex items-center gap-2 rounded-lg bg-sigma-700 px-4 py-2 text-sm font-semibold text-white hover:bg-sigma-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Copy className="h-4 w-4" />
              {isCloning ? 'Cloning...' : 'Clone to download'}
            </button>
          </div>
        </div>
      )}

      {isSharedGradableAssessment && (
        <div className="mx-auto mt-4 w-full max-w-[210mm] rounded-xl border border-sigma-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-gray-900">
                {assessment?.category === 'formal' ? 'Formal assessment' : 'Class practice assessment'}
              </div>
              <div className="text-xs text-gray-500">
                Response blocks are completed in assessment mode so answers can be saved, submitted, and graded.
              </div>
            </div>
            <button
              type="button"
              onClick={handleOpenGradableAssessment}
              className="inline-flex items-center gap-2 rounded-lg bg-sigma-700 px-4 py-2 text-sm font-semibold text-white hover:bg-sigma-800"
            >
              <Send className="h-4 w-4" />
              {isAuthenticated ? 'Start or Continue' : 'Sign In to Submit'}
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto mx-auto py-0 sm:py-4">
        {isGeometryDiagram ? (
          <div className="h-[calc(100vh-95px)] bg-white shadow-lg rounded-xl border border-gray-300">
            <GeometryTool
              fullScreen
              showSaveBar={false}
              readOnly
              allowTeachingModeInReadOnly
              initialDiagramData={document?.diagramData || {}}
              initialTitle={document?.title || document?.name || 'Shared Diagram'}
            />
          </div>
        ) : isPdfDocument ? (
          <PdfDocumentPanel
            pdf={document.pdf}
            canEdit={false}
            renderMode="canvas"
            audioPages={document?.narration?.pages || {}}
          />
        ) : (
          <div ref={pageContainerRef} className="mx-auto space-y-8" style={{ maxWidth: '210mm' }}>
            {pageNodes}
          </div>
        )}
      </div>

      {isSharedSelfPracticeAssessment && portalTargets.map(({ blockId, element }) => {
        const config = assessment?.responseAreas?.[blockId];
        if (!config) return null;
        return createPortal(
          <ResponseAreaRenderer
            key={blockId}
            blockId={blockId}
            config={config}
            value={responses[blockId]?.value ?? ''}
            onChange={(val) => handleResponseChange(blockId, val)}
            disabled={false}
            userId={user?.uid}
            enableModelAnswerToggle={isSharedSelfPracticeAssessment}
            canAccessModelAnswers={canRevealModelAnswers}
            modelAnswerLockedMessage="Sign in to reveal model answers."
            onModelAnswerLockedClick={() => setIsAuthModalOpen(true)}
          />,
          element
        );
      })}

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        initialMode="login"
      />
    </div>
  );
};

export default SharedDocumentPage;
